import React, { useMemo, useState } from 'react';
import { CharacterRole, GameState, StarSystem } from '../../types';
import { INCOME_RATES, ADMIN_BONUS_PER_LEVEL } from '../../shared/constants';
import { FLEET_COST_CREDITS, SHIPYARD_COST_CREDITS } from '../../shared/constants';
import { SHIP_STATS } from '../../shared/ships';

type Props = {
  system: StarSystem;
  gameState: GameState;
  myPlayerId: string | null;
  onBuildShip: (systemId: number, shipClass: 'frigate' | 'destroyer' | 'cruiser' | 'capital', targetFleetId?: number) => void;
  onBuildShipyard: (systemId: number) => void;
  onRepairFleet: (fleetId: number) => void;
  onMoveFleet: (fleetId: number, destinationId: number) => void;
  onAssignCharacterRole: (role: CharacterRole, targetId: number) => void;
};

export default function WorldPanel({ system, gameState, myPlayerId, onBuildShip, onBuildShipyard, onRepairFleet, onMoveFleet, onAssignCharacterRole }: Props) {
  const ownerName = gameState.players.find(p => p.id === system.owner)?.name || (system.owner === 'NPC' ? 'NPC' : 'Neutral');
  const me = myPlayerId ? gameState.players.find(p => p.id === myPlayerId) : null;

  const fleetsAtSystem = gameState.fleets.filter(f => f.location === system.id);
  const damagedFleets = gameState.fleets.filter(
    f => f.location === system.id && f.owner === myPlayerId && f.power < f.maxPower
  );

  // Ship class display costs and build ticks (mirror server defaults)
  const SHIP_COSTS = useMemo(() => ({
    frigate: SHIP_STATS.frigate.cost,
    destroyer: SHIP_STATS.destroyer.cost,
    cruiser: SHIP_STATS.cruiser.cost,
    capital: SHIP_STATS.capital.cost,
  } as const), []);
  const SHIP_BUILD_TICKS = useMemo(() => ({
    frigate: SHIP_STATS.frigate.buildTicks,
    destroyer: SHIP_STATS.destroyer.buildTicks,
    cruiser: SHIP_STATS.cruiser.buildTicks,
    capital: SHIP_STATS.capital.buildTicks,
  } as const), []);

  // Drag & Drop state for construction queue reordering
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleQueueDragStart = (_jobId: string | undefined, index: number, e: React.DragEvent<HTMLDivElement>) => {
    setDraggingIndex(index);
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleQueueDragOver = (index: number, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleQueueDrop = (index: number, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    const fromIndex = draggingIndex != null ? draggingIndex : Number.parseInt(data, 10);
    setDragOverIndex(null);
    setDraggingIndex(null);
    if (Number.isNaN(fromIndex) || fromIndex === index) return;
    if (!system.constructionQueue || system.constructionQueue.length < 2) return;
    (window as any).socket?.emit('reorderQueue', { systemId: system.id, fromIndex, toIndex: index });
  };

  const jobIndexFallback = (j: any) => (j && typeof j.queueIndex === 'number' ? j.queueIndex : -1);

  return (
    <div className="console-body">
      <div className="console-header">
        <div className="title">{system.name}</div>
        <div className="subtitle">{ownerName}</div>
      </div>

      <div className="card-grid">
        <div className="card">
          <div className="label">Planet</div>
          <div className="value">{system.planetType ? system.planetType.replace('_',' ') : 'Unknown'}</div>
        </div>
        <div className="card">
          <div className="label">Population</div>
          <div className="value">{system.population != null ? `${system.population.toFixed(1)}M / ${system.maxPopulation}M` : '—'}</div>
        </div>
        {typeof (system as any).fortification === 'number' && typeof (system as any).maxFortification === 'number' && (
          <div className="card">
            <div className="label">Fortification</div>
            <div className="value">{`${Math.floor((system as any).fortification)}/${Math.floor((system as any).maxFortification)}`}</div>
          </div>
        )}
        {system.hasShipyard && (
          <div className="card">
            <div className="label">Facility</div>
            <div className="value">Shipyard</div>
          </div>
        )}
        {system.owner === 'NPC' && (system as any).npcFactionId && (
          <div className="card">
            <div className="label">Faction</div>
            <div className="value">
              {(() => {
                const fac = (gameState as any).mapMeta?.npcFactions?.find((f: any) => f?.id === (system as any).npcFactionId);
                return fac ? fac.name : (system as any).npcFactionId;
              })()}
              {(system as any).isStronghold ? ' • Stronghold' : ''}
            </div>
          </div>
        )}
      </div>

      {system.owner !== 'NPC' && system.owner !== 'Unclaimed' && (
        <div className="card">
          <div className="label">Income/tick</div>
          <div className="row">
            {(() => {
              const rates = INCOME_RATES[system.planetType as unknown as keyof typeof INCOME_RATES] || { credits: 0, influence: 0 };
              const isGov = me && me.character.role === CharacterRole.Governor && me.character.assignment === system.id;
              const adminMult = isGov ? 1 + (me!.character.admin ?? 0) * ADMIN_BONUS_PER_LEVEL : 1;
              const pop = system.population != null ? system.population : 0;
              const credits = pop * rates.credits * adminMult;
              const influence = pop * rates.influence * adminMult;
              return (
                <>
                  <div className="pill">💰 +{credits.toFixed(1)}</div>
                  <div className="pill">✨ +{influence.toFixed(1)}</div>
                </>
              );
            })()}
            {me && me.character.role === CharacterRole.Governor && me.character.assignment === system.id && (
              <div className="pill good" title="Governor bonus">🟢 +{Math.round((me.character.admin ?? 0) * 5)}%</div>
            )}
          </div>
        </div>
      )}

      {(system as any).underSiegeBy && (
        <div className="card">
          <div className="label">Siege</div>
          <div className="row between">
            <div>Under siege by {(system as any).underSiegeBy === myPlayerId ? 'You' : (gameState.players.find(p => p.id === (system as any).underSiegeBy)?.name || (system as any).underSiegeBy)}</div>
            <div className="pill danger">⚠️ Bombardment</div>
          </div>
        </div>
      )}

      {fleetsAtSystem.length > 0 && (
        <div className="card">
          <div className="label">Fleet Status</div>
          <div className="stack">
            {fleetsAtSystem.map(f => (
              <div key={f.id} className="row between">
                <div>F{f.id} • {f.owner}</div>
                <div className="health">{Math.round(f.power)}/{Math.round(f.maxPower)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {system.constructionQueue && system.constructionQueue.length > 0 && (
        <div className="card">
          <div className="label">Construction Queue</div>
          <div className="stack">
            {system.constructionQueue.map((job, idx) => {
              const eta = Math.max(0, job.completionTick - gameState.tick);
              const label = job.type === 'ship' ? `${job.shipClass?.toUpperCase()}` : job.type.toUpperCase();
              const isDragOver = dragOverIndex === idx;
              return (
                <div
                  key={job.jobId || `${job.type}-${idx}-${job.completionTick}`}
                  className="row between"
                  style={{ alignItems: 'center', gap: 8, border: isDragOver ? '1px dashed #888' : undefined, padding: 4, borderRadius: 4 }}
                  draggable
                  onDragStart={(e) => handleQueueDragStart(job.jobId, idx, e)}
                  onDragOver={(e) => handleQueueDragOver(idx, e)}
                  onDrop={(e) => handleQueueDrop(idx, e)}
                >
                  <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                    <span style={{ cursor: 'grab' }} title="Drag to reorder">⋮⋮</span>
                    <div>{label}</div>
                  </div>
                  <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                    <div className="pill">t-{eta}</div>
                    <button
                      className="icon-btn danger"
                      title="Cancel this job"
                      onClick={() => (window as any).socket?.emit('cancelQueueItem', { systemId: system.id, index: idx })}
                    >✖</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {system.owner === myPlayerId && (
        <div className="stack">
          {system.hasShipyard ? (
            <div className="row gap">
              <button
                className={`action${(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.frigate ? ' disabled' : ''}`}
                disabled={(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.frigate}
                title={`t-${SHIP_BUILD_TICKS.frigate}`}
                onClick={() => onBuildShip(system.id, 'frigate')}
              >⚔️ Build Frigate <span className="cost">{SHIP_COSTS.frigate} 💰</span></button>

              <button
                className={`action${(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.destroyer ? ' disabled' : ''}`}
                disabled={(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.destroyer}
                title={`t-${SHIP_BUILD_TICKS.destroyer}`}
                onClick={() => onBuildShip(system.id, 'destroyer')}
              >🛡️ Build Destroyer <span className="cost">{SHIP_COSTS.destroyer} 💰</span></button>

              <button
                className={`action${(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.cruiser ? ' disabled' : ''}`}
                disabled={(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.cruiser}
                title={`t-${SHIP_BUILD_TICKS.cruiser}`}
                onClick={() => onBuildShip(system.id, 'cruiser')}
              >🛰️ Build Cruiser <span className="cost">{SHIP_COSTS.cruiser} 💰</span></button>

              <button
                className={`action${(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.capital ? ' disabled' : ''}`}
                disabled={(gameState.players.find(p => p.id === myPlayerId)?.resources.credits ?? 0) < SHIP_COSTS.capital}
                title={`t-${SHIP_BUILD_TICKS.capital}`}
                onClick={() => onBuildShip(system.id, 'capital')}
              >👑 Build Capital <span className="cost">{SHIP_COSTS.capital} 💰</span></button>
            </div>
          ) : (
            <div className="row gap">
              <button className="action disabled" disabled>🚀 No Shipyard</button>
              <button className="action secondary" onClick={() => onBuildShipyard(system.id)}>⚙️ Build Shipyard <span className="cost">{SHIPYARD_COST_CREDITS} 💰</span></button>
            </div>
          )}

          {system.hasShipyard && damagedFleets.map(fleet => {
            const damagePercent = fleet.maxPower > 0 ? 1 - (fleet.power / fleet.maxPower) : 0;
            const repairCost = Math.floor(50 * Math.max(0, damagePercent));
            return (
              <button key={fleet.id} className="action" onClick={() => onRepairFleet(fleet.id)}>🔧 Repair F{fleet.id} <span className="cost">{repairCost} 💰</span></button>
            );
          })}
          {system.hasShipyard && damagedFleets.length > 1 && (
            <button className="action secondary" onClick={() => damagedFleets.forEach(f => onRepairFleet(f.id))}>🧰 Repair All ({damagedFleets.length})</button>
          )}
        </div>
      )}

      {system.owner === myPlayerId && me && (
        <div className="card">
          <div className="label">Leadership</div>
          <div className="row between">
            <div><strong>{me.character.name}</strong> — {CharacterRole[me.character.role]}</div>
            {(me.character.role === CharacterRole.Unassigned || (me.character.role === CharacterRole.Governor && me.character.assignment !== system.id)) && (
              <button className="action secondary" onClick={() => onAssignCharacterRole(CharacterRole.Governor, system.id)}>Appoint Governor</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


